import {
  useMe,
  useSendServerSideTrackEvent,
  useSendServerSidePageEvent,
} from '~/libs/api-hooks/api/private';
import { useCallback, useMemo } from 'react';
import { ServerEvents } from '~/libs/analytics/components/Analytics';
import { EventPropertiesSnakeCase } from '~/libs/analytics/types/EventProperties';

const useSetupServer = (
  isOuterPage = false,
): {
  server: ServerEvents | undefined;
  isSuccess: boolean;
  isError: boolean;
} => {
  const {
    data: me,
    isSuccess: meIsSuccess,
    isError: meIsError,
    error: meError,
  } = useMe({
    reactQueryOptions: {
      retry: false,
      retryOnMount: false,
      notifyOnChangeProps: ['isSuccess', 'isError'],
      enabled: !isOuterPage,
    },
  });
  const OptOut = useMemo<boolean>(() => {
    return Boolean(
      (meIsSuccess && me?.privacyOptout) ||
        (meIsError && !meError?.isUnauthorizedError()),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meIsError, meIsSuccess]);
  const { mutateAsync: trackServer } = useSendServerSideTrackEvent();
  const { mutateAsync: pageServer } = useSendServerSidePageEvent();
  const page = useCallback(
    async (pageName: string, properties: EventPropertiesSnakeCase) => {
      if (OptOut) {
        return;
      }

      return pageServer({
        userAnalyticsId:
          me?.analyticsId ||
          properties.user_id ||
          properties.circleci_anonymous_id ||
          properties.anonymous_id ||
          '',
        pageName,
        properties,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [OptOut],
  );
  const track = useCallback(
    async (eventName: string, properties: EventPropertiesSnakeCase) => {
      if (OptOut) {
        return;
      }

      return trackServer({
        userAnalyticsId:
          me?.analyticsId ||
          properties.user_id ||
          properties.circleci_anonymous_id ||
          properties.anonymous_id ||
          '',
        eventName,
        properties,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [OptOut],
  );
  const server: ServerEvents = useMemo<ServerEvents>(
    () => ({
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      page,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      track,
    }),
    [page, track],
  );

  return {
    server: server,
    isSuccess: !OptOut,
    isError: Boolean(meIsError && !meError?.isUnauthorizedError()),
  };
};

export default useSetupServer;
